import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import {
  GetOnHandReportInput,
  useGetStockOnHandReportQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { StatusFilter } from "@saleor/requiredAttention/types";
import StockOnHandFilter from "@saleor/stockOnHand/components/StockOnHandFilter";
import StockOnHandTableResult from "@saleor/stockOnHand/components/StockOnHandTableResult";
import { stockOnHandUrlParam } from "@saleor/stockOnHand/url";
import {
  StockOnHandSort,
  StockOnHandSortField
} from "@saleor/stockOnHand/views/ReportStockOnHandPage/sort";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";

interface ReportStockOnHandPageProps {
  params: StockOnHandSort;
}

export interface filterStockOnHand {
  materialGradeGram: string;
  unit: string;
  type: string;
}

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  () => ({
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    }
  }),
  { name: "ReportStockOnHandPage" }
);

const ReportStockOnHandPage: React.FC<ReportStockOnHandPageProps> = ({
  params
}) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [{ fullScreen }] = useAppState();
  const classes = useStyles({ fullScreen });
  const [filter, setFilter] = useState<filterStockOnHand>({
    materialGradeGram: "",
    unit: "",
    type: "Customer"
  });
  const [filterPlant, setFilterPlant] = useState<string>("All");

  const listFilterArr = Object.values(filter);

  const queryVariable: GetOnHandReportInput = useMemo(
    () => ({
      type: filter.type,
      unit: filter.unit,
      code: filter.materialGradeGram
    }),
    [filter]
  );

  const {
    data: dataStockOnHand,
    loading: loadingStockOnHand
  } = useGetStockOnHandReportQuery({
    variables: {
      input: queryVariable
    },
    skip: listFilterArr.filter(item => item).length < 2,
    displayLoader: true,
    sourceSystem: "Iplan"
  });

  const statusFilter: StatusFilter = useMemo(() => {
    if (listFilterArr.filter(item => item).length < 2 || loadingStockOnHand) {
      return StatusFilter.INFO;
    } else if (
      listFilterArr.filter(item => item).length > 1 &&
      !dataStockOnHand?.getStockOnHandReport
    ) {
      return StatusFilter.ERROR;
    } else if (
      listFilterArr.filter(item => item).length > 1 &&
      dataStockOnHand?.getStockOnHandReport
    ) {
      return StatusFilter.SUCCESS;
    }
    return StatusFilter.INFO;
  }, [listFilterArr]);

  const handleSort = field => {
    navigate(
      stockOnHandUrlParam({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  const sort = useMemo(() => getSortParams(params), [params]);

  const filterByPlant = item =>
    filterPlant === "All" ? true : item.plant === filterPlant;

  const listStockOnHand = useMemo(() => {
    if (!dataStockOnHand?.getStockOnHandReport) {
      return null;
    }
    const result = {
      ...dataStockOnHand?.getStockOnHandReport,
      customerOrderList: dataStockOnHand?.getStockOnHandReport?.customerOrderList
        ?.filter(item => filterByPlant(item))
        .map(item => ({ ...item, writable: true })),
      consignmentList: dataStockOnHand?.getStockOnHandReport?.consignmentList
        ?.filter(item => filterByPlant(item))
        .map(item => ({ ...item, writable: true })),
      freeStockList: dataStockOnHand?.getStockOnHandReport?.freeStockList
        ?.filter(item => filterByPlant(item))
        .map(item => ({ ...item, writable: true }))
    };

    switch (sort.sort) {
      case StockOnHandSortField.customer:
      default:
        if (sort.asc) {
          result.customerOrderList.sort((itemA, itemB) =>
            itemA.customer.localeCompare(itemB.customer)
          );
          result.consignmentList.sort((itemA, itemB) =>
            itemA.customerSession.localeCompare(itemB.customerSession)
          );
        } else {
          result.customerOrderList.sort((itemA, itemB) =>
            itemB.customer.localeCompare(itemA.customer)
          );
          result.consignmentList.sort((itemA, itemB) =>
            itemB.customerSession.localeCompare(itemA.customerSession)
          );
        }
        break;
      case StockOnHandSortField.quantity:
        if (sort.asc) {
          result.freeStockList.sort(
            (itemA, itemB) => itemA.quantity - itemB.quantity
          );
        } else {
          result.freeStockList.sort(
            (itemA, itemB) => itemB.quantity - itemA.quantity
          );
        }
    }
    return result;
  }, [dataStockOnHand, sort, filterPlant]);

  return (
    <Container>
      <PageHeader
        className={classes.pageHeader}
        title={intl.formatMessage({
          defaultMessage: "Stock on-hand",
          description: "label"
        })}
      />
      <StockOnHandFilter
        setFilter={setFilter}
        statusFilter={statusFilter}
        filterArr={listFilterArr}
        loading={loadingStockOnHand}
        dataStockOnHand={listStockOnHand}
        setFilterPlant={setFilterPlant}
      />
      {listStockOnHand ? (
        <StockOnHandTableResult
          dataStockOnHand={listStockOnHand}
          sort={getSortParams(params)}
          onSort={handleSort}
          filterPlant={filterPlant}
          setFilterPlant={setFilterPlant}
        />
      ) : null}
    </Container>
  );
};

export default ReportStockOnHandPage;
